import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, gbp } from "../../lib/apiClient";
import { useAuth } from "../../lib/AuthContext";
import { buildFloorAuditCsv, downloadCsv } from "../../lib/floorAuditCsv";
import { Button } from "../../components/ui/button";
import { Download, BarChart3, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { FloorAuditSettingsDialog } from "./FloorAuditSettingsDialog";

const TH = "px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-slate-500 whitespace-nowrap";
const TD = "px-3 py-2 text-sm whitespace-nowrap";

const Money = ({ v, dark, testid }) => {
  if (v == null) return null;
  const n = Number(v);
  const color = n < 0 ? (dark ? "text-red-400" : "text-red-600") : n > 0 ? (dark ? "text-emerald-400" : "text-emerald-700") : "text-slate-400";
  return <span className={`font-mono font-semibold ${color}`} data-testid={testid}>{n < 0 ? "-" : n > 0 ? "+" : ""}{gbp(Math.abs(n))}</span>;
};

const DateInput = ({ label, value, onChange, testid }) => (
  <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">{label}
    <input type="date" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="block mt-1 h-10 border border-slate-300 rounded px-2 text-sm" />
  </label>
);

const SummaryCards = ({ summary }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-4 gap-3" data-testid="floor-audit-summary">
    {summary.map((m) => {
      const dark = m.key === "all" || m.key === "net";
      const tid = `floor-audit-metric-${m.key.replace(/[^a-z0-9]+/g, "-")}`;
      return (
        <div key={m.key} data-testid={tid}
          className={`rounded-lg border p-4 ${dark ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200"}`}>
          <div className={`text-[11px] uppercase tracking-wider font-semibold ${dark ? "text-white/70" : "text-slate-500"}`}>{m.label}</div>
          <div className="mt-2 flex items-baseline justify-between gap-2">
            <span className="font-display text-3xl font-black" data-testid={`${tid}-count`}>{m.count ?? ""}</span>
            <Money v={m.value} dark={dark} testid={`${tid}-value`} />
          </div>
        </div>
      );
    })}
  </div>
);

const estCols = (showOver) => [
  ["Quotes", (e) => e.quotes],
  ["Hours under", (e) => e.hours_under_count], ["Hours under £", (e) => <Money v={e.hours_under} />],
  ...(showOver ? [["Hours over", (e) => e.hours_over_count], ["Hours over £", (e) => <Money v={e.hours_over} />]] : []),
  ["Mark-up under", (e) => e.markup_under_count], ["Mark-up under £", (e) => <Money v={e.markup_under} />],
  ...(showOver ? [["Mark-up over", (e) => e.markup_over_count], ["Mark-up over £", (e) => <Money v={e.markup_over} />]] : []),
  ["Repairs under", (e) => e.repair_count], ["Repair £", (e) => <Money v={e.repair_under} />],
  ["Under any floor", (e) => <>{e.any_count} <span className="text-slate-500">({e.any_pct}%)</span></>],
  ["All under £", (e) => <Money v={e.all_under} />],
];

const EstimatorTable = ({ rows, showOver }) => {
  const cols = estCols(showOver);
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
      <table className="min-w-full" data-testid="floor-audit-estimators">
        <thead className="bg-slate-50"><tr><th className={TH}>Estimator</th>{cols.map(([h]) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.estimator} className="border-t border-slate-100" data-testid={`floor-audit-est-${e.estimator.toLowerCase().replace(/\s+/g, "-")}`}>
              <td className={`${TD} font-semibold`}>{e.estimator}</td>
              {cols.map(([h, f]) => <td key={h} className={TD}>{f(e)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Notes = ({ items, v }) => (items.length ? <div className="text-xs">{items.join("; ")} <Money v={v} /></div> : null);

const QuoteTable = ({ rows, showOver }) => (
  <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white max-h-[520px]">
    <table className="min-w-full" data-testid="floor-audit-quotes">
      <thead className="bg-slate-50 sticky top-0"><tr>
        {["Quote", "Created", "Estimator", "Customer", "Labour Type", "Job", "Hours", "Mark-up", "Repair", "Current", "At floors", "Under £"]
          .map((h) => <th key={h} className={TH}>{h}</th>)}
      </tr></thead>
      <tbody>
        {rows.map((q) => (
          <tr key={q.id} className="border-t border-slate-100 align-top">
            <td className={TD}><Link to={`/quotes/${q.id}`} className="text-blue-700 hover:underline font-semibold" data-testid={`floor-audit-quote-link-${q.id}`}>{q.quote_number || "—"}</Link></td>
            <td className={TD}>{q.created}</td>
            <td className={TD}>{q.estimator}</td>
            <td className={TD}>{q.customer}</td>
            <td className={TD}>{q.labour_type}</td>
            <td className={TD}>{q.job_type}</td>
            <td className={TD}><Notes items={q.hours_under_notes} v={q.hours_under} />{showOver && <Notes items={q.hours_over_notes} v={q.hours_over} />}</td>
            <td className={TD}>
              <Notes items={q.markup_under_notes} v={q.markup_under} />{showOver && <Notes items={q.markup_over_notes} v={q.markup_over} />}
              {(q.markup_under_notes.length || (showOver && q.markup_over_notes.length)) ? <div className="text-[11px] text-slate-500">default {q.expected_markup}%</div> : null}
            </td>
            <td className={TD}>{q.repair_short != null && <div className="text-xs">{q.repair_hours}h (short {q.repair_short}h) <Money v={q.repair_under} /></div>}</td>
            <td className={`${TD} font-mono`}>{gbp(q.base)}</td>
            <td className={`${TD} font-mono`}>{gbp(q.total_at_floors)}</td>
            <td className={TD}><Money v={q.all_under} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export function FloorAuditTab() {
  const { user } = useAuth();
  const [start, setStart] = useState("2026-09-01");
  const [end, setEnd] = useState("2026-09-30");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  if (user?.role !== "admin") return <div data-testid="floor-audit-forbidden" className="text-sm text-slate-500">Admins only.</div>;

  const run = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.get("/reports/floor-audit", { params: { start, end } });
      setData(d);
    } catch {
      toast.error("Could not run the floor audit.");
    } finally {
      setBusy(false);
    }
  };

  const download = () => downloadCsv(buildFloorAuditCsv(data), `floor-audit-${start}-to-${end}.csv`);
  const showOver = data?.settings?.show_over;

  return (
    <div className="space-y-6" data-testid="floor-audit-tab">
      <p className="text-sm text-slate-600 max-w-2xl">
        Quotes that went under the recommended install hours, the default mark-up, or the repair minimum.
        Money lost is shown in <span className="text-red-600 font-semibold">red (-)</span>, money gained above the floor in
        <span className="text-emerald-700 font-semibold"> green (+)</span>. Totals ex VAT.
      </p>
      <div className="flex items-end gap-3 flex-wrap">
        <DateInput label="From" value={start} onChange={(v) => { setStart(v); setData(null); }} testid="floor-audit-start" />
        <DateInput label="To" value={end} onChange={(v) => { setEnd(v); setData(null); }} testid="floor-audit-end" />
        <Button onClick={run} disabled={busy || !start || !end} data-testid="floor-audit-run-btn">
          <BarChart3 size={14} className="mr-2" />{busy ? "Running…" : "Run audit"}
        </Button>
        <Button variant="outline" onClick={download} disabled={!data} data-testid="floor-audit-download-btn">
          <Download size={14} className="mr-2" />Download CSV
        </Button>
        <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="floor-audit-settings-btn">
          <Settings2 size={14} className="mr-2" />Report settings
        </Button>
      </div>
      <FloorAuditSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} onSaved={() => data && run()} />
      {data && (
        <div className="space-y-6" data-testid="floor-audit-results">
          <SummaryCards summary={data.summary} />
          <div className="space-y-2">
            <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700">Per estimator</h3>
            <EstimatorTable rows={data.estimators} showOver={showOver} />
          </div>
          <div className="space-y-2">
            <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700">Flagged quotes ({data.quotes.length})</h3>
            {data.quotes.length ? <QuoteTable rows={data.quotes} showOver={showOver} /> : <p className="text-sm text-slate-500" data-testid="floor-audit-empty">No quotes went under a floor in this period.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
